import React, { useState } from 'react';
import {
  Button, Modal, ModalHeader, ModalBody, ModalFooter,
  Alert, Spinner
} from 'reactstrap';
import { RepositoryFactory } from '../repositories';
import { createModuleLogger } from '../utils/logger';

// Crear logger para este módulo
const finalizarLogger = createModuleLogger('FinalizarOrdenButton');

const FinalizarOrdenButton = ({ orden, onFinalizada, onShowAlert }) => {
  // Estados del componente
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Inicializar repositorio
  const ordenRepository = RepositoryFactory.getRepository('orden_produccion');

  // Obtener nombres amigables desde posibles ubicaciones (con y sin expand)
  // Nota: PocketBase devuelve relaciones expandidas bajo orden.expand.<relacion>
  const clienteNombre = (
    orden?.expand?.cliente?.razon_social ||
    orden?.cliente?.razon_social ||
    orden?.cliente?.nombre ||
    null
  ) || 'N/A';

  const productoNombre = (
    orden?.nombre_producto ||
    orden?.expand?.producto?.nombre ||
    orden?.producto?.nombre ||
    null
  ) || 'N/A';

  // Función para obtener usuario autenticado
  const getCurrentUser = () => {
    try {
      const authUser = localStorage.getItem("authUser");
      if (authUser) {
        const userData = JSON.parse(authUser);
        return userData.record?.id;
      }
      return null;
    } catch (error) {
      finalizarLogger.error('Error al obtener usuario autenticado', { error: error.message });
      return null;
    }
  };

  // Función para abrir modal
  const openModal = () => {
    setError(null);
    setModalOpen(true);
  };

  // Función para cerrar modal
  const closeModal = () => {
    setModalOpen(false);
    setError(null);
    setLoading(false);
  };

  // Función para finalizar orden
  const handleFinalizarOrden = async () => {
    try {
      setLoading(true);
      setError(null);

      // Verificar autenticación
      const usuarioId = getCurrentUser();
      if (!usuarioId) {
        throw new Error('Usuario no autenticado');
      }

      // Verificar que la orden no esté ya finalizada
      if (orden.finalizada && orden.finalizada !== "") {
        throw new Error('La orden ya está finalizada');
      }

      finalizarLogger.info('Iniciando finalización de orden', { 
        ordenId: orden.id, 
        numero: orden.numero,
        usuarioId 
      });

      // Llamar al repositorio para finalizar la orden
      await ordenRepository.finalizarOrden(orden.id, usuarioId);

      finalizarLogger.info('Orden finalizada exitosamente', { 
        ordenId: orden.id, 
        numero: orden.numero 
      });

      // Mostrar mensaje de éxito
      if (onShowAlert) {
        onShowAlert('success', 'Orden finalizada exitosamente');
      }

      // Cerrar modal
      closeModal();

      // Notificar que la orden fue finalizada
      if (onFinalizada) {
        onFinalizada();
      }

    } catch (error) {
      const errorMessage = `Error al finalizar orden: ${error.message}`;
      finalizarLogger.error(errorMessage, { 
        error: error.message, 
        ordenId: orden.id,
        numero: orden.numero 
      });

      setError(errorMessage);

      // Mostrar mensaje de error
      if (onShowAlert) {
        onShowAlert('danger', errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // Verificar si la orden ya está finalizada
  const isOrdenFinalizada = orden.finalizada && orden.finalizada !== "";

  return (
    <>
      {/* Botón de Finalizar Orden */}
      <Button
        color="success"
        size="sm"
        onClick={openModal}
        disabled={isOrdenFinalizada}
        title={isOrdenFinalizada ? 'Orden ya finalizada' : 'Finalizar orden de producción'}
        data-testid="finalizar-orden-btn"
      >
        <i className="fas fa-check"></i>
        {isOrdenFinalizada ? ' Finalizada' : ' Finalizar'}
      </Button>

      {/* Modal de Confirmación */}
      <Modal isOpen={modalOpen} toggle={closeModal} size="md">
        <ModalHeader toggle={closeModal}>
          Confirmar Finalización de Orden
        </ModalHeader>
        <ModalBody>
          {error && (
            <Alert color="danger">
              {error}
            </Alert>
          )}

          <div className="text-center mb-3">
            <i className="fas fa-question-circle fa-3x text-warning"></i>
          </div>

          <p className="text-center">
            ¿Estás seguro de que deseas finalizar la orden <strong>#{orden.numero}</strong>?
          </p>

          <div className="alert alert-info">
            <h6>Información de la Orden:</h6>
            <ul className="mb-0">
              <li><strong>Cliente:</strong> {clienteNombre}</li>
              <li><strong>Producto:</strong> {productoNombre}</li>
            </ul>
          </div>

          <div className="alert alert-warning">
            <i className="fas fa-exclamation-triangle"></i>
            <strong> Atención:</strong> Esta acción no se puede deshacer. 
            La orden será marcada como finalizada y se creará un registro de LogEstado.
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeModal} disabled={loading}>
            Cancelar
          </Button>
          <Button 
            color="success" 
            onClick={handleFinalizarOrden} 
            disabled={loading}
            data-testid="confirmar-finalizar-btn"
          >
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Finalizando...
              </>
            ) : (
              <>
                <i className="fas fa-check me-2"></i>
                Confirmar Finalización
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default FinalizarOrdenButton;
